import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import { MessageCircle, Images, Film, User, Bell, Camera, CloudSun, CircleHelp, Ellipsis, Sun, Moon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTheme } from "@/lib/theme-context";
import { cn } from "@/lib/utils";
import { useConversations } from "@/hooks/useConversations";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuth } from "@/lib/auth-context";
import { hexToHslString, foregroundHslFor } from "@/lib/color";
import { CallProvider } from "@/hooks/useCall";
import { GroupCallProvider } from "@/hooks/useGroupCall";
import { LiveLocationProvider } from "@/hooks/useLiveLocation";
import CallOverlay from "@/components/calls/CallOverlay";
import GroupCallOverlay from "@/components/calls/GroupCallOverlay";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import SkLogo from "@/components/SkLogo";
import { FloatingChatsProvider, useFloatingChats } from "@/hooks/useFloatingChats";
import FloatingChatWindow from "@/components/chat/FloatingChatWindow";

// The mobile bottom bar's own 4 tabs, unchanged. The desktop sidebar (below)
// reuses this same list plus Notifications - there's simply more room on a
// wide screen for a nav item the mobile bar can't fit without crowding.
const tabs = [
  { to: "/chat", labelKey: "nav.chat", icon: MessageCircle },
  { to: "/snap", labelKey: "nav.snap", icon: Camera },
  { to: "/feed", labelKey: "nav.feed", icon: Images },
  { to: "/reels", labelKey: "nav.reels", icon: Film },
  { to: "/profile", labelKey: "nav.profile", icon: User }
];

   const sidebarTabs = [...tabs.slice(0, -1), { to: "/notifications", labelKey: "nav.notifications", icon: Bell }, tabs[tabs.length - 1]];
   
export default function AppLayout() {
  return (
    <CallProvider>
      <GroupCallProvider>
      <LiveLocationProvider>
      <FloatingChatsProvider>
        <AppLayoutContent />
      </FloatingChatsProvider>
      </LiveLocationProvider>
      </GroupCallProvider>
    </CallProvider>
  );
}

function AppLayoutContent() {
  const { t } = useTranslation();
  const { data: conversations } = useConversations();
  const { data: notifications } = useNotifications();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  // Reels and the single-post view both want a genuinely full-height,
  // full-bleed area (a full-screen player/media surface), not the shared
  // max-width content column every other page uses - constraining either
  // of them the normal way left mobile viewports with media that
  // collapsed to its own content size instead of filling the screen,
  // since the shared wrapper only ever got an explicit height at the md
  // breakpoint and up (percentage heights need a parent with a DEFINITE
  // height to resolve against - "no explicit height below md" resolves
  // to auto, not "0, so shrink to content" the way it looked in testing).
  const isFullBleed = location.pathname.startsWith("/reels") || location.pathname.startsWith("/post/");

   useEffect(() => {
     const root = document.documentElement;
     if (user?.accent_color) {
       const hsl = hexToHslString(user.accent_color);
       const fg = foregroundHslFor(user.accent_color);
       if (hsl && fg) {
         root.style.setProperty("--primary", hsl);
         root.style.setProperty("--ring", hsl);
         root.style.setProperty("--primary-foreground", fg);
         return;
       }
     }
     root.style.removeProperty("--primary");
     root.style.removeProperty("--ring");
     root.style.removeProperty("--primary-foreground");
   }, [user?.accent_color]);
   

  // The "Open in app" card (OpenInAppBanner) sits above the phone tab bar: tell it how tall the bar is while this layout is mounted.
  useEffect(() => {
    document.documentElement.style.setProperty("--bottom-nav-h", "3.5rem");
    return () => {
      document.documentElement.style.removeProperty("--bottom-nav-h");
    };
  }, []);

  // Mirrors exactly which conversations ConversationRow ends up hiding (confirmed
  // undecryptable on this device) - never fetches anything itself, just
  // subscribes to the same cache entry ConversationRow writes into once it
  // confirms a conversation is unreadable here, so the badge count can exclude
  // it too. Without this, the badge could show an unread count for a
  // conversation that's been hidden from the list entirely, with nothing to
  // click to clear it.
  const undecryptableFlags = useQueries({
    queries: (conversations ?? []).map((c) => ({
      queryKey: ["conversation-undecryptable", c.id],
      queryFn: () => false,
      staleTime: Infinity,
      gcTime: Infinity
    }))
  });
  const undecryptableIds = new Set(
    (conversations ?? []).filter((c, i) => undecryptableFlags[i]?.data === true).map((c) => c.id)
  );

  const unreadMessages =
    conversations?.reduce((sum, c) => (undecryptableIds.has(c.id) ? sum : sum + c.unread_count), 0) ?? 0;
  const unreadNotifications = notifications?.filter((n) => !n.is_read).length ?? 0;

  function badgeFor(to: string) {
    if (to === "/chat") return unreadMessages;
    if (to === "/notifications") return unreadNotifications;
    return 0;
  }

  const { openChatIds, openFloatingChat, closeFloatingChat } = useFloatingChats();
  const [dragOverSide, setDragOverSide] = useState(false);

  function handleFloatingDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOverSide(false);
    const id = e.dataTransfer.getData("application/x-conversation-id");
    if (id) openFloatingChat(id);
  }

  return (
      <div className="flex h-screen bg-background">
        {/* Desktop sidebar */}
   <aside className="hidden shrink-0 flex-col border-r border-border px-3 py-5 md:flex md:w-64">
     <NavLink to="/chat" aria-label="Sksnap" className="mb-5 flex items-center gap-3 px-3">
       <SkLogo size={36} />
       <span className="text-xl font-extrabold tracking-tight">Sksnap</span>
     </NavLink>
     <nav className="flex flex-col gap-1">
       {sidebarTabs.map(({ to, labelKey, icon: Icon }) => {
         const badge = badgeFor(to);
         return (
           <NavLink
             key={to}
             to={to}
             className={({ isActive }) =>
               cn(
                 "flex items-center gap-4 rounded-full px-3 py-3 text-base transition-colors hover:bg-secondary",
                 isActive ? "font-extrabold text-foreground [&_svg]:stroke-[2.75]" : "font-medium text-foreground/80"
               )
             }
           >
             <span className="relative">
               <Icon className="h-7 w-7" />
               {badge > 0 && (
                 <span className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-notify px-1 text-xs font-bold text-white">
                   {badge > 9 ? "9+" : badge}
                 </span>
               )}
             </span>
             {t(labelKey)}
           </NavLink>
         );
       })}
     </nav>
     <div className="mt-auto">
       <DropdownMenu>
         <DropdownMenuTrigger asChild>
           <button type="button" className="flex w-full items-center gap-4 rounded-full px-3 py-3 text-base font-medium text-foreground/80 transition-colors hover:bg-secondary">
             <Ellipsis className="h-7 w-7" />
             {t("ux.more")}
           </button>
         </DropdownMenuTrigger>
         <DropdownMenuContent align="start" side="top" className="w-60">
           <DropdownMenuItem onClick={() => navigate("/weather")}><CloudSun className="mr-3 h-5 w-5" />{t("nav.weather")}</DropdownMenuItem>
           <DropdownMenuItem onClick={() => navigate("/help")}><CircleHelp className="mr-3 h-5 w-5" />{t("help.home")}</DropdownMenuItem>
           <DropdownMenuItem onClick={toggleTheme}>
             {theme === "dark" ? <Sun className="mr-3 h-5 w-5" /> : <Moon className="mr-3 h-5 w-5" />}
             {theme === "dark" ? "Light mode" : "Dark mode"}
           </DropdownMenuItem>
         </DropdownMenuContent>
       </DropdownMenu>
     </div>
   </aside>
   

        {/* Content column */}
        <div className="mx-auto flex h-screen w-full max-w-lg flex-1 flex-col bg-background md:mx-0 md:max-w-none">
          <EmailVerificationBanner />
          <main
            className={cn("relative flex-1 overflow-y-auto pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0", dragOverSide && "md:bg-secondary/60")}
            onDragOver={(e) => {
              // Only the empty side margins (outside the centered content
              // column) should accept this drop - e.target being the main
              // element itself (not a descendant) is how we tell the two
              // apart, since the inner column sits on top of it.
              if (e.target !== e.currentTarget) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
              setDragOverSide(true);
            }}
            onDragLeave={(e) => {
              if (e.target === e.currentTarget) setDragOverSide(false);
            }}
            onDrop={handleFloatingDrop}
          >
            {isFullBleed ? (
              <Outlet />
            ) : (
              <div className="md:mx-auto md:h-full md:max-w-2xl">
                <Outlet />
              </div>
            )}
          </main>

          {/* Mobile-only bottom tab bar */}
   <nav aria-label="Primary" className="fixed bottom-0 z-10 w-full max-w-lg border-t border-border bg-background pb-[env(safe-area-inset-bottom)] md:hidden">
     <div className="flex h-14 items-center">
       {tabs.map(({ to, labelKey, icon: Icon }) => {
         const badge = badgeFor(to);
         return (
           <NavLink key={to} to={to} aria-label={t(labelKey)}
             className={({ isActive }) => cn("relative flex h-14 flex-1 items-center justify-center", isActive ? "text-foreground" : "text-muted-foreground")}>
             {({ isActive }) => (
               <>
                 <Icon className={cn("h-[26px] w-[26px]", isActive && "stroke-[2.75]")} />
                 {badge > 0 && (
                   <span className="absolute left-1/2 top-2.5 ml-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-notify px-1 text-xs font-bold text-white">
                     {badge > 9 ? "9+" : badge}
                   </span>
                 )}
               </>
             )}
           </NavLink>
         );
       })}
     </div>
   </nav>
   
        </div>

        <CallOverlay />
        <GroupCallOverlay />

        {/* Floating chat heads - desktop only (mobile has no empty side
            space to have dragged one into in the first place). Fixed to the
            viewport, so they stay visible across navigation. */}
        <div className="pointer-events-none fixed inset-0 z-40 hidden md:block">
          {openChatIds.map((id, i) => (
            <FloatingChatWindow key={id} conversationId={id} offset={i} onClose={() => closeFloatingChat(id)} />
          ))}
        </div>
      </div>
  );
}
